import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Spin, message } from 'antd';
import CategoryCard from './CategoryCard';
import ProductList from './ProductList';
import SkeletonLoader from './SkeletonLoader';
import './AppointmentTypeV2.css';
import {
  isExpandable,
  isExternal,
  EXTERNAL_URL,
  sanitizeForCallback,
  stripHtml,
  buildCategoriesFromStorage,
} from './config';
import useDebounce from '../../Common/useDebounce';
import { Config } from '../../Common/Config';
import axios from 'axios';
import generateSessionEventPush, { pushTraceLogOnClose, pushTraceLogEvent, logInteraction } from '../sessionEventPush';

const AppointmentTypeV2 = (props) => {
  const {
    getAllHealthScreenData,
    callback,
    callBackForbackpage,
    hashCode,
    ContinueButoonStylingMiddle,
  } = props;

  const [messageApi, contextHolder] = message.useMessage();

  // ── Category state ─────────────────────────────────────────────────────────
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedProductId, setSelectedProductId]   = useState(null);
  const [selectedProductData, setSelectedProductData] = useState(null);
  const [validationMsg, setValidationMsg] = useState('');

  // ── Corporate state ────────────────────────────────────────────────────────
  const [corporateSearchQuery, setCorporateSearchQuery] = useState('');
  const [corporateResults, setCorporateResults]         = useState([]);
  const [corporateLoading, setCorporateLoading]         = useState(false);
  const [selectedCorporate, setSelectedCorporate]       = useState(null);
  const [showCorporateProducts, setShowCorporateProducts] = useState(false);
  const debouncedCorporateSearch = useDebounce(corporateSearchQuery, 500);

  const continueRef = useRef(null);

  // ── Load categories from localStorage ────────────────────────────────────
  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let cats = buildCategoriesFromStorage();

      // Fallback: If localStorage is empty, fetch from API directly
      if (cats.length === 0 && localStorage.getItem('token')) {
        const url = `${Config.url}product-category/page/1?per_page=-1`;
        const response = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        pushTraceLogEvent({
          event: "product_category_fetched",
          payload: { api: "product-category/page/1" },
          step_status: "success",
          response: response?.data,
        });

        const rawList = response?.data?.data?.data;
        if (rawList && rawList.length) {
          let categoryMap = {};
          rawList.forEach(cat => {
            if (cat?.product_category_id) {
              categoryMap[`category${cat.product_category_id}`] = cat;
            }
          });
          if (Object.keys(categoryMap).length > 0) {
            localStorage.setItem('showCategory', JSON.stringify(categoryMap));
            cats = buildCategoriesFromStorage(); // Re-build to ensure consistent sorting
          }
        }
      }

      // hashCode flow → only show corporate category
      if (hashCode) {
        cats = cats.filter(c => c.type === 'corporate');
      }
      setCategories(cats);
    } catch (err) {
      console.error("Failed to load appointment categories:", err);
      pushTraceLogEvent({
        event: "product_category_fetch_failed",
        payload: { api: "product-category/page/1", message: err?.response?.data?.message || err?.message },
        step_status: "failed",
      });
      setError('Failed to load appointment types. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [hashCode]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // ── Seed the trace-log session as soon as the booking journey opens ────────
  // Previously the ONLY generateSessionEventPush() call (which seeds trace_log_id
  // by hitting trace-log/init) fired inside handleContinue, i.e. only once the
  // admin finished Step 1. Every pushTraceLogEvent() call before that point —
  // across this entire file and every later step of the journey — silently
  // no-ops because trace_log_id doesn't exist in localStorage yet. Seeding it
  // here, on mount, means clicks from the very first interaction are captured.
  useEffect(() => {
    generateSessionEventPush({
      module: 'booking',
      log_name: 'BookingJourney',
      event: 'booking_journey_opened',
      payload: {
        screen: 'step_1',
        user_type: 'CSR',
        date: new Date().toISOString().split('T')[0],
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Visibility close event ─────────────────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pushTraceLogOnClose({
          event: 'browser_closed_or_minimized',
          payload: { step: 'appointment_type_selection' },
          step_status: 'success',
          meta: { reason: 'tab_close_or_background' },
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // ── Corporate search ───────────────────────────────────────────────────────
  const fetchCorporateData = async (keyword) => {
    if (!keyword?.trim()) { setCorporateResults([]); return; }
    setCorporateLoading(true);
    try {
      const res = await axios.get(
        `${Config.url}get-admin-corporates?keyword=${encodeURIComponent(keyword)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      pushTraceLogEvent({
        event: "corporate_search_fetched",
        payload: { api: "get-admin-corporates", keyword },
        step_status: "success",
        response: res?.data,
      });
      setCorporateResults(res.data?.data || []);
    } catch (err) {
      pushTraceLogEvent({
        event: "corporate_search_failed",
        payload: { api: "get-admin-corporates", keyword, message: err?.response?.data?.message || err?.message },
        step_status: "failed",
      });
      messageApi.error({ content: 'Failed to fetch corporate data' });
      setCorporateResults([]);
    } finally {
      setCorporateLoading(false);
    }
  };

  const selectedCategory = categories.find(c => c.product_category_id === selectedCategoryId) || null;

  useEffect(() => {
    if (selectedCategory?.type === 'corporate') {
      fetchCorporateData(debouncedCorporateSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCorporateSearch, selectedCategory?.type]);

  const handleCorporateSelect = (corporate) => {
    logInteraction("corporate_selected", { corporate_id: corporate?.id, corporate_name: corporate?.name });
    setSelectedCorporate(corporate);
    setShowCorporateProducts(true);
    setCorporateSearchQuery(corporate.name);
    setCorporateResults([]);
    // Reset product — user must select from corporate's product list if available
    setSelectedProductId(null);
    setSelectedProductData(null);

    // If this corporate company has no active products, auto-continue immediately
    const corpProducts = corporate?.corporate_products || [];

    if (corpProducts.length === 0) {
      handleContinue({
        corporate: corporate,
        productId: null,
        productData: null,
      });
    }
  };

  const resetCorporate = () => {
    setSelectedCorporate(null);
    setShowCorporateProducts(false);
    setSelectedProductId(null);
    setSelectedProductData(null);
  };

  // ── All non-GP products from real API (flat array) ───────────────────────
  // Uses the same access pattern as the rest of the codebase:
  //   props.getAllHealthScreenData.getAllHealthScreenData.data = products array
  const allProductData = getAllHealthScreenData?.getAllHealthScreenData?.data || [];

  // True while the parent hasn't yet resolved the products API call.
  // Parent initialises the state as [] so the prop arrives as
  // { getAllHealthScreenData: [] } immediately — checking the prop itself
  // is always truthy. Instead we check for the actual .data array which
  // only exists once the API response has been set.
  const isProductsLoading = !getAllHealthScreenData?.getAllHealthScreenData?.data;

  // ── Display categories (corporate label override & empty filter) ─────────
  const displayCategories = categories.filter(cat => {
    // For D2C/Health Screen categories, only show them if they have mapped products
    if (isExpandable(cat) && cat.type !== 'corporate') {
      const mappedProducts = allProductData.filter(p => p.product_category_id == cat.product_category_id);
      if (mappedProducts.length === 0) return false;
    }
    return true;
  }).map(cat => {
    if (
      cat.type === 'corporate' &&
      selectedCorporate?.change_category_label &&
      selectedCorporate?.product_category_label
    ) {
      return { ...cat, name: selectedCorporate.product_category_label };
    }
    return cat;
  });

  // ── Category click ────────────────────────────────────────────────────────
  const handleCategoryClick = (category) => {
    logInteraction("category_clicked", {
      category_id: category.product_category_id,
      category_name: category.name,
      category_type: category.type,
    });

    // Video — open Semble external link
    if (isExternal(category)) {
      window.open(EXTERNAL_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    const catId = category.product_category_id;

    if (selectedCategoryId === catId) {
      // Clicking same category: for expandable, collapse; for direct (GP), keep selected
      if (isExpandable(category)) {
        setSelectedCategoryId(null);
        setSelectedProductId(null);
        setSelectedProductData(null);
        resetCorporate();
        setCorporateSearchQuery('');
      }
      // GP already selected → nothing changes (user hits Continue)
    } else {
      setSelectedCategoryId(catId);
      setSelectedProductId(null);
      setSelectedProductData(null);
      resetCorporate();
      setCorporateSearchQuery('');

      if (!isExpandable(category)) {
        handleContinue({
          categoryId: catId,
          category: category,
          productId: null,
          productData: null,
          corporate: null,
        });
      }
    }
  };

  const handleProductSelect = (productId, product) => {
    logInteraction("product_selected", { product_id: productId, product_name: product?.name });
    setSelectedProductId(productId);
    setSelectedProductData(product);
    
    handleContinue({
      productId: productId,
      productData: product,
    });
  };

  // ── Continue ──────────────────────────────────────────────────────────────
  const handleContinue = (overrideState = {}) => {
    const finalCatId = overrideState.categoryId !== undefined ? overrideState.categoryId : selectedCategoryId;
    const finalCat = overrideState.category !== undefined ? overrideState.category : selectedCategory;
    const finalProdId = overrideState.productId !== undefined ? overrideState.productId : selectedProductId;
    const finalProdData = overrideState.productData !== undefined ? overrideState.productData : selectedProductData;
    const finalCorporate = overrideState.corporate !== undefined ? overrideState.corporate : selectedCorporate;

    if (!finalCatId) return;
    
    if (finalCat && isExpandable(finalCat) && !finalProdId) {
      // Special case: If corporate, and the selected corporate has NO active products, allow proceeding
      const isCorporateWithoutProducts = 
        finalCat.type === 'corporate' && 
        finalCorporate && 
        (!finalCorporate.corporate_products || finalCorporate.corporate_products.length === 0);
        
      if (!isCorporateWithoutProducts) {
        return;
      }
    }

    // Build final category object (may have overridden name for corporate)
    const baseCategory = displayCategories.find(c => c.product_category_id === finalCatId) || finalCat;
    
    let finalCategoryObj = baseCategory;
    if (
      baseCategory &&
      baseCategory.type === 'corporate' &&
      finalCorporate?.change_category_label &&
      finalCorporate?.product_category_label
    ) {
      finalCategoryObj = { ...baseCategory, name: finalCorporate.product_category_label };
    }

    // NOTE: was generateSessionEventPush() — that hits trace-log/init and would
    // mint a SECOND, different trace_log_id here, orphaning every event logged
    // between mount and now under the session already started above. This now
    // appends to that same session instead of starting a new one.
    pushTraceLogEvent({
      event: 'booking_flow_started',
      payload: {
        appointment_type:      finalCategoryObj?.product_category_id,
        appointment_type_name: finalCategoryObj?.name,
        selected_product:      finalProdData?.name || finalCorporate?.name || null,
        selected_product_id:   finalProdId || null,
        corporate_id:          finalCorporate?.id || null,
        screen: 'step_1',
        user_type: 'CSR',
        selected_date: new Date().toISOString().split('T')[0],
      },
      step_status: 'success',
    });

    const payload = sanitizeForCallback({
      productCategory:    finalCategoryObj,
      non_gp_product_id:  finalProdId || null,
      productData:        finalProdData || null,
      selectedCorporate:  finalCorporate || undefined,
    });

    if (callback) callback(payload, 'BookingForm');
  };

  // ── Back ──────────────────────────────────────────────────────────────────
  const handleBack = () => {
    if (callBackForbackpage) callBackForbackpage(null);
  };

  // ── Render states ──────────────────────────────────────────────────────────
  // Wait for both: categories (localStorage sync) AND products (async prop)
  if (loading || isProductsLoading) return <SkeletonLoader />;

  if (error) return (
    <div className="aptv2-error-state" role="alert">
      <div className="aptv2-error-icon">⚠️</div>
      <p className="aptv2-error-title">Something went wrong</p>
      <p className="aptv2-error-msg">{error}</p>
      <button className="aptv2-retry-btn" onClick={() => { logInteraction("category_load_retry_clicked", {}); loadCategories(); }} type="button">
        <ReloadOutlined /> Try again
      </button>
    </div>
  );

  if (!loading && displayCategories.length === 0) return (
    <div className="aptv2-empty-state">
      <div className="aptv2-empty-icon">🏥</div>
      <p className="aptv2-empty-title">No appointment types available</p>
      <p className="aptv2-empty-msg">
        Please refresh the page or log in again to load appointment types.
      </p>
    </div>
  );

  // const showBackButton = !ContinueButoonStylingMiddle && !!callBackForbackpage;

  return (
    <div className="aptv2-wrapper">
      {contextHolder}

      {/* Header */}
      <div className="aptv2-header">
        {selectedCorporate?.company_logo && (
          <img
            src={selectedCorporate.company_logo}
            alt="Company logo"
            className="aptv2-corp-logo"
          />
        )}
        <div className="aptv2-header-band">
          <h2>Appointment type</h2>
        </div>
      </div>

      {/* Category list + accordions */}
      <div className="aptv2-body">
        <div className="aptv2-category-list" role="list">
          {displayCategories.map((category) => {
            const catId    = category.product_category_id;
            const isSelected = selectedCategoryId === catId;

            // Products for any expandable non-corporate category (d2c, health, etc.)
            // Direct filter on the raw array — same pattern as the old working code.
            // Loose == so string/number product_category_id both match.
            const isProductCategory = isExpandable(category) && category.type !== 'corporate';
            const d2cProducts = isProductCategory
              ? allProductData
                  .filter(p => p.product_category_id == catId)
                  .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.name || '').localeCompare(b.name || ''))
              : [];

            // Corporate products from the selected corporate's own product list
            const corpProducts =
              category.type === 'corporate' &&
              showCorporateProducts &&
              selectedCorporate?.corporate_products
                ? selectedCorporate.corporate_products
                : [];

            return (
              <div key={catId} role="listitem">
                {/* Category card */}
                <CategoryCard
                  category={category}
                  isSelected={isSelected}
                  onClick={handleCategoryClick}
                />

                {/* ── Corporate: search box + product list ── */}
                {category.type === 'corporate' && isSelected && (
                  <div className="aptv2-corporate-section">
                    <div className="aptv2-corporate-search-wrapper">
                      <Input
                        placeholder="Search corporate..."
                        prefix={<SearchOutlined style={{ color: '#008385' }} />}
                        value={corporateSearchQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCorporateSearchQuery(val);
                          if (!val || (selectedCorporate && val !== selectedCorporate.name)) {
                            resetCorporate();
                          }
                        }}
                        allowClear
                        className="corporate-search-input"
                        size="large"
                      />

                      {/* Dropdown */}
                      {!showCorporateProducts && (
                        corporateLoading ||
                        corporateResults.length > 0 ||
                        (debouncedCorporateSearch && corporateResults.length === 0)
                      ) && (
                        <div className="corporate-dropdown-list">
                          {corporateLoading && (
                            <div className="corporate-dropdown-loading">
                              <Spin className="custom-spinner" tip="Searching..." />
                            </div>
                          )}
                          {!corporateLoading && corporateResults.map((corp, idx) => (
                            <div
                              key={corp.id || idx}
                              className="corporate-dropdown-item"
                              onClick={() => handleCorporateSelect(corp)}
                            >
                              <div className="corporate-dropdown-item-name">{corp.name}</div>
                              {corp.phone && (
                                <div className="corporate-dropdown-item-phone">{corp.phone}</div>
                              )}
                            </div>
                          ))}
                          {!corporateLoading && debouncedCorporateSearch && corporateResults.length === 0 && (
                            <div className="corporate-dropdown-empty">No corporates found</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Corporate product list */}
                    {showCorporateProducts && corpProducts.length > 0 && (
                      <div
                        className="aptv2-product-list-outer"
                        style={{ maxHeight: '9999px', overflow: 'visible' }}
                      >
                        <div
                          className="aptv2-product-list-inner"
                          role="radiogroup"
                          aria-label="Select a service"
                        >
                          {corpProducts.map((product, idx) => {
                            const pid    = product.non_gp_product_id?.toString();
                            const isSel  = selectedProductId === pid;
                            return (
                              <div
                                key={product.non_gp_product_id || idx}
                                className={`aptv2-product-item${isSel ? ' aptv2-product-item--selected' : ''}`}
                                onClick={() => handleProductSelect(pid, product)}
                                tabIndex={0}
                                role="radio"
                                aria-checked={isSel}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleProductSelect(pid, product);
                                  }
                                }}
                              >
                                <div className={`aptv2-radio${isSel ? ' aptv2-radio--checked' : ''}`}>
                                  {isSel && <div className="aptv2-radio-dot" />}
                                </div>
                                <div className="aptv2-product-content">
                                  <span className="aptv2-product-name">{product.name}</span>
                                  <span
                                    className="appt-prod-dec"
                                    style={{
                                      textWrap: "inherit",
                                      textAlign: "left",
                                      fontSize: "13px",
                                      color: "#6b7280",
                                      marginTop: "4px"
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: product.product_specific_booking_page_short_copy || product.product_specific_booking_page_long_copy || "",
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Product accordion for all expandable non-corporate types ── */}
                {isExpandable(category) && category.type !== 'corporate' && (
                  <ProductList
                    products={d2cProducts}
                    selectedProductId={selectedProductId}
                    categoryId={catId}
                    onSelect={handleProductSelect}
                    isVisible={isSelected}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default AppointmentTypeV2;
